import Button from "components/ui/Button";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Edit, Trash2, Plus, X } from "lucide-react"; // أضفت أيقونات للأزرار

const AttributeSection = ({
  attributes,
  onAddAttribute,
  hasAttributes,
  onHasAttributesChange,
  quantity,
  onSetQuantity,
  onDeleteAttribute, // callback لحذف attribute
  onUpdateAttribute, // callback لتحديث attribute
}) => {
  const { t } = useTranslation();

  // Attribute states (محدثة لتتناسب مع البنية الجديدة)
  const [newAttribute, setNewAttribute] = useState({
    translations: { ar: { name: "" } },
    values: [],
  });

  const [newValue, setNewValue] = useState({ translations: { ar: { value: "" } } });

  // state للتعديل
  const [editingAttribute, setEditingAttribute] = useState(null); // index اللي قيد التعديل
  const [editAttribute, setEditAttribute] = useState({
    translations: { ar: { name: "" } },
    values: [],
  });
  const [editValue, setEditValue] = useState({ translations: { ar: { value: "" } } });

  const handleAddValue = () => {
    if (newValue.translations.ar.value) {
      setNewAttribute((prev) => ({
        ...prev,
        values: [...prev.values, { ...newValue }],
      }));
      setNewValue({ translations: { ar: { value: "" } } });
    }
  };

  const handleAddAttribute = () => {
    if (newAttribute.translations.ar.name && newAttribute.values.length > 0) {
      onAddAttribute(newAttribute);
      setNewAttribute({
        translations: { ar: { name: "" } },
        values: [],
      });
    }
  };

  const handleSetQuantity = () => {
    if (quantity && onSetQuantity) {
      onSetQuantity(parseInt(quantity, 10) || 0);
    }
  };

  const handleCheckboxChange = (e) => {
    if (onHasAttributesChange) {
      onHasAttributesChange(e.target.checked);
    }
    if (!e.target.checked) {
      setNewAttribute({ translations: { ar: { name: "" } }, values: [] });
      setNewValue({ translations: { ar: { value: "" } } });
      setEditingAttribute(null); // أغلق التعديل
    }
  };

  // وظائف للتعديل والحذف
  const handleEditAttribute = (index) => {
    setEditingAttribute(index);
    setEditAttribute({ ...attributes[index] });
    setEditValue({ translations: { ar: { value: "" } } });
  };

  const handleDeleteAttribute = (index) => {
    if (onDeleteAttribute) {
      onDeleteAttribute(index);
    }
  };

  const handleSaveEdit = () => {
    if (onUpdateAttribute) {
      onUpdateAttribute(editingAttribute, editAttribute);
      setEditingAttribute(null);
      setEditAttribute({ translations: { ar: { name: "" } }, values: [] });
    }
  };

  const handleCancelEdit = () => {
    setEditingAttribute(null);
    setEditAttribute({ translations: { ar: { name: "" } }, values: [] });
  };

  const handleAddEditValue = () => {
    if (editValue.translations.ar.value) {
      setEditAttribute((prev) => ({
        ...prev,
        values: [...prev.values, { ...editValue }],
      }));
      setEditValue({ translations: { ar: { value: "" } } });
    }
  };

  const handleRemoveEditValue = (valIndex) => {
    setEditAttribute((prev) => ({
      ...prev,
      values: prev.values.filter((_, i) => i !== valIndex),
    }));
  };

  if (!hasAttributes) {
    return (
      <div className="bg-white p-6 rounded-lg shadow-lg border border-gray-200">
        <h2 className="text-2xl font-bold mb-6 text-gray-800">{t("attributes_title")}</h2>

        <div className="mb-4">
          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={hasAttributes}
              onChange={handleCheckboxChange}
              className="rounded"
            />
            <span className="text-gray-700">{"المنتج له مواصفات"}</span>
          </label>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2 text-gray-700">
              {t("quantity_label") || "الكمية"}
            </label>
            <input
              type="number"
              min="0"
              value={quantity}
              onChange={(e) => onSetQuantity(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white p-6 rounded-lg shadow-lg border border-gray-200">
      <h2 className="text-2xl font-bold mb-6 text-gray-800">{t("attributes_title")}</h2>

      <div className="mb-4">
        <label className="flex items-center space-x-2">
          <input
            type="checkbox"
            checked={hasAttributes}
            onChange={handleCheckboxChange}
            className="rounded"
          />
          <span className="text-gray-700">{"المنتج له مواصفات"}</span>
        </label>
      </div>

      <div className="space-y-6">
        {/* قسم إضافة attribute جديد */}
        <div className="bg-gray-50 p-4 rounded-lg border">
          <h3 className="text-lg font-semibold mb-4 text-gray-800">إضافة مواصفة جديدة</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2 text-gray-700">
                {t("attribute_name_ar")}
              </label>
              <input
                type="text"
                value={newAttribute.translations.ar.name}
                onChange={(e) =>
                  setNewAttribute((prev) => ({
                    ...prev,
                    translations: {
                      ...prev.translations,
                      ar: { ...prev.translations.ar, name: e.target.value },
                    },
                  }))
                }
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="أدخل اسم المواصفة"
              />
            </div>
          </div>

          <div className="border-t pt-4 space-y-4">
            <h4 className="font-medium text-gray-800">{t("attribute_values_title")}</h4>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-700">
                  {t("value_ar")}
                </label>
                <input
                  type="text"
                  value={newValue.translations.ar.value}
                  onChange={(e) =>
                    setNewValue((prev) => ({
                      ...prev,
                      translations: {
                        ...prev.translations,
                        ar: { ...prev.translations.ar, value: e.target.value },
                      },
                    }))
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="أدخل القيمة"
                />
              </div>
            </div>

            <button
              type="button"
              onClick={handleAddValue}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
            >
              <Plus size={16} />
              {t("add_value")}
            </button>

            <div className="space-y-2 mt-4">
              {newAttribute.values.map((value, index) => (
                <div
                  key={index}
                  className="bg-white p-3 rounded-lg border flex justify-between items-center"
                >
                  <span className="font-medium text-gray-800">{value?.translations?.ar?.value}</span>
                  <button
                    type="button"
                    onClick={() =>
                      setNewAttribute((prev) => ({
                        ...prev,
                        values: prev.values.filter((_, i) => i !== index),
                      }))
                    }
                    className="text-red-500 hover:text-red-700"
                  >
                    <X size={16} />
                  </button>
                </div>
              ))}
            </div>
          </div>

          <Button
            type="button"
            onClick={handleAddAttribute}
            disabled={!newAttribute.translations.ar.name || newAttribute.values.length === 0}
            className="mt-4 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400"
          >
            {t("add_attribute")}
          </Button>
        </div>
      </div>

      {/* قسم عرض وتعديل الـ attributes الموجودة */}
      {attributes.length > 0 && (
        <div className="mt-8">
          <h3 className="text-lg font-semibold mb-4 text-gray-800">{t("added_attributes_title")}</h3>
          <div className="space-y-4">
            {attributes.map((attr, attrIndex) => (
              <div key={attrIndex} className="bg-gray-50 p-4 rounded-lg border">
                {editingAttribute === attrIndex ? (
                  // نموذج التعديل
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-2 text-gray-700">
                        {t("attribute_name_ar")}
                      </label>
                      <input
                        type="text"
                        value={editAttribute.translations.ar.name}
                        onChange={(e) =>
                          setEditAttribute((prev) => ({
                            ...prev,
                            translations: {
                              ...prev.translations,
                              ar: { ...prev.translations.ar, name: e.target.value },
                            },
                          }))
                        }
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2 text-gray-700">
                        {t("attribute_values_title")}
                      </label>
                      <input
                        type="text"
                        value={editValue.translations.ar.value}
                        onChange={(e) =>
                          setEditValue((prev) => ({
                            ...prev,
                            translations: {
                              ...prev.translations,
                              ar: { ...prev.translations.ar, value: e.target.value },
                            },
                          }))
                        }
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 mb-2"
                        placeholder="أضف قيمة جديدة"
                      />
                      <button
                        type="button"
                        onClick={handleAddEditValue}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                      >
                        <Plus size={16} />
                        {t("add_value")}
                      </button>
                      <div className="space-y-2 mt-4">
                        {editAttribute.values.map((value, valIndex) => (
                          <div
                            key={valIndex}
                            className="bg-white p-3 rounded-lg border flex justify-between items-center"
                          >
                            <span className="text-gray-800">{value?.translations?.ar?.value}</span>
                            <button
                              type="button"
                              onClick={() => handleRemoveEditValue(valIndex)}
                              className="text-red-500 hover:text-red-700"
                            >
                              <X size={16} />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button onClick={handleSaveEdit} className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">
                        {t("save")}
                      </Button>
                      <Button onClick={handleCancelEdit} variant="outline" className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100">
                        {t("cancel")}
                      </Button>
                    </div>
                  </div>
                ) : (
                  // عرض عادي
                  <div>
                    <div className="font-semibold text-gray-800">{attr?.translations?.ar?.name}</div>
                    <div className="mt-3 space-y-2">
                      {attr.values.map((value, valueIndex) => (
                        <div key={valueIndex} className="text-sm text-gray-600 bg-white p-2 rounded border">
                          {value?.translations?.ar?.value}
                        </div>
                      ))}
                    </div>
                    <div className="mt-4 flex gap-2">
                      <Button
                        onClick={() => handleEditAttribute(attrIndex)}
                        size="sm"
                        className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center gap-1"
                      >
                        <Edit size={14} />
                        {t("edit")}
                      </Button>
                      <Button
                        onClick={() => handleDeleteAttribute(attrIndex)}
                        variant="destructive"
                        size="sm"
                        className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 flex items-center gap-1"
                      >
                        <Trash2 size={14} />
                        {t("delete")}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AttributeSection;
